import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React from 'react';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getColumnOptionDatas} from 'insights/analytics-visualizations/configs/config-components/ConfigEditorUtils';
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {
  getDataFrameInfoOrEmptyInfo,
  useStepConfigEditorContext,
} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import * as logging from 'util/Logging';

import {
  DataFrameFilter,
  DataFrameFilterOperation,
  isFilterStepConfigVariableRegularShape,
} from '../DataFrameFilterTypes';
import {convertFromInternalFilterInterface, convertToInternalFilterInterface} from './convertFilterInterface';
import InternalDataFrameFilterCreator from './InternalDataFrameFilterCreator';

const EMPTY_CONFIG: PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0>['filter'] = {
  operation: 'AND',
  filters: [],
};

function createDefaultFilter(columnSystemName: string): DataFrameFilter {
  return {
    operation: DataFrameFilterOperation.AND,
    filters: [{operation: DataFrameFilterOperation.IS_NULL, columnSystemName, not_: true}],
  };
}

interface FilterConfigProps {
  config: PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0>['filter'] | null;
  onChangeConfig: (newConfig: PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0>['filter']) => void;
  disabled?: boolean;
}

/**
 * Filter step configuration, which is compatible with the `generalized-step-config-editor` module.
 *
 * This currently only supports configuring the regularized shape of a filter, supported by the Analysis UI,
 * which has exactly two levels of nesting. See `DataFrameFilterTypes.ts`.
 */
export default function FilterStepFilterConfig({
  config: originalConfig,
  onChangeConfig,
  disabled,
}: FilterConfigProps): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();
  const {dataFrameSpec} = getDataFrameInfoOrEmptyInfo(dataFramesInfos, SINGLE_DATASET_INPUT_STEP_DATA_NAME);
  const config = originalConfig ?? EMPTY_CONFIG;

  if (!isFilterStepConfigVariableRegularShape(config)) {
    logging.warn('Unexpected filter shape');
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="This filter is no longer valid." />;
  }

  return (
    <div {...getTestProps({component: 'FilterStepFilterConfig', element: 'root'})}>
      {config.filters.map((filter, index) => (
        <SingleColumnFilterConfig
          key={index}
          index={index}
          filter={filter}
          onChangeFilter={(newFilter) => {
            onChangeConfig(
              produce(config, (draftConfig) => {
                if (newFilter.filters.length === 0) {
                  // Remove a column's filter, if its final condition has been removed
                  draftConfig.filters.splice(index, 1);
                } else {
                  draftConfig.filters[index] = newFilter;
                }
              })
            );
          }}
          disabled={disabled}
          dataFrameSpec={dataFrameSpec}
        />
      ))}
      {!disabled && (
        <Tooltip isDisabled={dataFrameSpec.columns.length > 0} text="No columns are available to filter">
          <AddItemButton
            onClick={() => {
              onChangeConfig(
                produce(config, (draftConfig) => {
                  draftConfig.filters.push(createDefaultFilter(dataFrameSpec.columns[0].systemName));
                })
              );
            }}
            label="Add column filter"
            disabled={dataFrameSpec.columns.length === 0}
            dataTestAttrs={{component: 'FilterStepFilterConfig', element: 'addColumnFilterButton'}}
          />
        </Tooltip>
      )}
    </div>
  );
}

interface SingleColumnFilterConfigProps {
  filter: DataFrameFilter;
  onChangeFilter: (newFilter: DataFrameFilter) => void;
  dataFrameSpec: DataFrameSpec;
  disabled?: boolean;
  index: number;
}

function SingleColumnFilterConfig({
  filter,
  onChangeFilter,
  dataFrameSpec,
  disabled,
  index,
}: SingleColumnFilterConfigProps): JSX.Element {
  const columnSystemName = filter.filters.length > 0 ? filter.filters[0].columnSystemName : null;
  const column = dataFrameSpec.columns.find((column) => column.systemName === columnSystemName);

  return (
    <div
      className={css(styles.columnFilter)}
      {...getTestProps({component: 'FilterStepFilterConfig', element: 'columnFilter', index: `${index}`})}
    >
      <div className={css(styles.columnSelectorAndLabel)}>
        <FieldLabel label="Column to filter" />
        <div className={css(styles.columnSelectorAndDeleteIcon)}>
          <div className={css(styles.columnSelector)}>
            <DropdownSelector
              allowNull={false}
              onChange={(columnSystemName: string) => {
                onChangeFilter(createDefaultFilter(columnSystemName));
              }}
              searchable={true}
              selectedValue={columnSystemName}
              optionDatas={getColumnOptionDatas(dataFrameSpec.columns, true)}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
      {column && (
        <InternalDataFrameFilterCreator
          column={column}
          conjunction={filter.operation}
          disabled={disabled}
          filters={(filter.filters ?? []).map((condition) => convertToInternalFilterInterface(condition))}
          onFiltersChange={(conjunction, newFilters): void => {
            onChangeFilter({
              operation: conjunction,
              filters: newFilters.map((newFilter) => convertFromInternalFilterInterface(newFilter)),
            });
          }}
        />
      )}
      {column == null && (
        <InlineBanner bannerType={InlineBannerType.WARNING} text="The selected column no longer exists." />
      )}
    </div>
  );
}

export const styles = StyleSheet.create({
  columnFilter: {
    padding: 10,
    background: Colors.GRAY_LIGHTER,
    marginBottom: 14,
  },
  columnSelectorAndLabel: {
    marginBottom: 15,
  },
  columnSelectorAndDeleteIcon: {
    display: 'flex',
  },
  columnSelector: {
    width: '100%',
  },
});
